import type { ISCEDelegateProvider } from "angular";

import type { StateProvider, Transition, UrlMatcherFactory, UrlService } from "@uirouter/angularjs";

import { Permission } from "App/Modules/Models";
import type { IFeatureFlagService } from "App/Modules/Services";
import type { IConfig } from "App/Modules/Utils";
import type { IMmState } from "./Interfaces";

export default [
    "$sceProvider",
    "$sceDelegateProvider",
    "$urlServiceProvider",
    "$stateProvider",
    "irConfig",
    "$breadcrumbProvider",
    "$animateProvider",
    "$urlMatcherFactoryProvider",
    function appConfig(
        $sceProvider: any,
        $sceDelegateProvider: ISCEDelegateProvider,
        $urlServiceProvider: UrlService,
        $stateProvider: StateProvider,
        config: IConfig,
        $breadcrumbProvider: any,
        $animateProvider: any,
        $urlMatcherFactoryProvider: UrlMatcherFactory,
    ) {
        $sceProvider.enabled(false);
        // Used to whitelist urls for http requests from angular
        $sceDelegateProvider.resourceUrlWhitelist(["self", `${config.apiUrl}**`]);

        $urlServiceProvider.rules.otherwise("/");
        $urlServiceProvider.config.caseInsensitive(true);

        $breadcrumbProvider.setOptions({
            includeAbstract: true,
            template: `<ol class="breadcrumb">
                <li ng-repeat="step in steps" ng-class="{active: $last}">
                    <a ng-if="!$last" href="{{step.ncyBreadcrumbLink}}" style='transition: 0s'>
                    {{step.ncyBreadcrumbLabel}}</a>
                    <span ng-if="$last" style='transition: 0s'>{{step.ncyBreadcrumbLabel}}</span>
                </li>
            </ol>`,
        });

        $animateProvider.classNameFilter(/^(?:(?!ng-animate-disabled)(?!aa-notification).)*$/);

        const translateReady = [
            "$translate",
            ($translate: angular.translate.ITranslateService) => $translate.onReady(),
        ];

        const dashboardState: IMmState = {
            component: "mmDashboard",
            name: "dashboard",
            ncyBreadcrumb: {
                label: "{{ 'BeforeLogin.Common.Home' | translate }}",
            },
            permission: { allowAnonymous: false },
            url: "/",
        };

        const sitesListState: IMmState = {
            component: "mmSitesList",
            name: "sites",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Site.Sites' | translate }}",
                skip: true,
                parent: "dashboard",
            },
            permission: { sitePermission: Permission.SiteInformation },
            resolve: {
                translateReady,
            },
            url: "/sites",
        };

        const siteLayoutState: IMmState = {
            abstract: true,
            name: "sites.site",
            ncyBreadcrumb: {
                label: "{{$ctrl.site.data.name}}",
                parent: "sites",
            },
            resolve: {
                featureFlags: [
                    "irFeatureFlagService",
                    (irFeatureFlagService: IFeatureFlagService) => irFeatureFlagService.waitForLDClient(),
                ],
                siteId: ["$transition$", ($transition$: Transition) => $transition$.params().siteId],
                translateReady,
            },
            url: "/:siteId",
            views: {
                "@": {
                    component: "mmSiteLayout",
                },
            },
        };

        const siteOverviewState: IMmState = {
            name: "sites.site.overview",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Manage.SideMenu.Overview' | translate }}",
                parent: "sites.site",
            },
            permission: { partnerPermission: Permission.SiteInformation },
            resolve: {
                siteId: ["$transition$", ($transition$: Transition) => $transition$.params().siteId],
                translateReady,
            },
            url: "",
            views: {
                site: {
                    component: "mmSite",
                },
            },
        };

        const siteEventsState: IMmState = {
            name: "sites.site.events",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Site.Events' | translate }}",
                parent: "sites.site",
            },
            permission: { sitePermission: Permission.SiteInformation },
            resolve: {
                siteId: ["$transition$", ($transition$: Transition) => $transition$.params().siteId],
                translateReady,
            },
            url: "/events",
            views: {
                site: {
                    component: "irEvents",
                },
            },
        };

        const siteBasicInformationState: IMmState = {
            name: "sites.site.basicInformation",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Site.BasicInformation' | translate }}",
                parent: "sites.site",
            },
            permission: { sitePermission: Permission.SiteInformation },
            resolve: {
                siteId: ["$transition$", ($transition$: Transition) => $transition$.params().siteId],
                translateReady,
            },
            url: "/basicInformation",
            views: {
                site: {
                    component: "mmSiteBasicInformation",
                },
            },
        };

        const siteUsersState: IMmState = {
            name: "sites.site.users",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.User.Users' | translate }}",
                parent: "sites.site",
            },
            permission: { sitePermission: Permission.SiteInformation },
            resolve: {
                siteId: ["$transition$", ($transition$: Transition) => $transition$.params().siteId],
                translateReady,
            },
            url: "/users",
            views: {
                site: {
                    component: "mmSiteUsersList",
                },
            },
        };

        const siteConfigurationGroupsState: IMmState = {
            name: "sites.site.configurationGroups",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.ConfigurationGroup.ConfigurationGroups' | translate }}",
                parent: "sites.site",
            },
            permission: { sitePermission: Permission.SiteInformation },
            resolve: {
                siteId: ["$transition$", ($transition$: Transition) => $transition$.params().siteId],
                translateReady,
            },
            url: "/settings",
            views: {
                site: {
                    component: "mmSiteConfigurationGroups",
                },
            },
        };

        const siteDesignAndPlanningState: IMmState = {
            name: "sites.site.designAndPlanning",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Site.QuickSetupDesignAndPlanning' | translate}}",
                parent: "sites.site",
            },
            permission: { sitePermission: Permission.SiteInformation },
            resolve: {
                siteId: ["$transition$", ($transition$: Transition) => $transition$.params().siteId],
                translateReady,
            },
            url: "/designAndPlanning",
            views: {
                site: {
                    component: "mmDesignAndPlanning",
                },
            },
        };

        const siteEquipmentState: IMmState = {
            name: "sites.site.equipment",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Equipment.Equipments' | translate}}",
                parent: "sites.site",
            },
            permission: { sitePermission: Permission.SiteInformation },
            resolve: {
                siteId: ["$transition$", ($transition$: Transition) => $transition$.params().siteId],
                translateReady,
            },
            url: "/equipment",
            views: {
                site: {
                    component: "irSiteEquipment",
                },
            },
        };

        const siteSystemsTestingState: IMmState = {
            name: "sites.site.systemsTesting",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Manage.SideMenu.SystemsTesting' | translate}}",
                parent: "sites.site",
            },
            permission: {
                partnerPermission: Permission.PartnerOperations,
                sitePermission: Permission.GlobalMonitoring,
            },
            resolve: {
                siteId: ["$transition$", ($transition$: Transition) => $transition$.params().siteId],
            },
            url: "/systemsTesting",
            views: {
                site: {
                    component: "irSystemsTesting",
                },
            },
        };

        const siteActivationState: IMmState = {
            name: "sites.site.activation",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Site.Activation' | translate}}",
                parent: "sites.site",
            },
            permission: {
                sitePermission: Permission.PartnerSettings,
            },
            resolve: {
                siteId: ["$transition$", ($transition$: Transition) => $transition$.params().siteId],
                translateReady,
            },
            url: "/activation",
            views: {
                site: {
                    component: "irSiteActivation",
                },
            },
        };

        const alarmCentralsListState: IMmState = {
            component: "alarmCentrals",
            name: "alarmCentrals",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.AlarmCentral.AlarmCentrals' | translate }}",
                parent: "dashboard",
            },
            permission: { partnerPermission: Permission.GlobalSysAdmin },
            resolve: {
                translateReady,
            },
            url: "/alarmCentrals",
        };

        const alarmCentralState: IMmState = {
            name: "alarmCentrals.alarmCentral",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.AlarmCentral.AlarmCentral' | translate }}",
                parent: "alarmCentrals",
            },
            permission: { partnerPermission: Permission.GlobalSysAdmin },
            resolve: {
                alarmCentralId: ["$transition$", ($transition$: Transition) => $transition$.params().alarmCentralId],
                translateReady,
            },
            url: "/:alarmCentralId",
            views: {
                "@": {
                    component: "alarmCentralDetails",
                },
            },
        };

        const profileState: IMmState = {
            component: "userProfileDetailsDataContainer",
            name: "profile",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Header.Settings' | translate }}",
                parent: "dashboard",
            },
            permission: { allowAnonymous: false },
            resolve: {
                translateReady,
            },
            url: "/profile",
        };

        const translationKeysState: IMmState = {
            component: "mmTranslationKeys",
            name: "translationKeys",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.TranslationKey.TranslationKeys' | translate }}",
                parent: "dashboard",
            },
            permission: { partnerPermission: Permission.DevelopmentTesting },
            resolve: {
                translateReady,
            },
            url: "/translationKeys",
        };

        const translationsState: IMmState = {
            component: "mmTranslations",
            name: "translations",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Translation.Translations' | translate }}",
                parent: "dashboard",
            },
            permission: { partnerPermission: Permission.GlobalSysAdmin },
            resolve: {
                translateReady,
            },
            url: "/translations",
        };

        const aboutState: IMmState = {
            component: "irAbout",
            name: "about",
            permission: { allowAnonymous: true },
            url: "/about",
        };

        const loginState: IMmState = {
            component: "mmLogin",
            name: "login",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Login.Login' | translate}}",
            },
            permission: { allowAnonymous: true },
            url: "/login",
        };

        const teamsState: IMmState = {
            component: "teams",
            name: "teams",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Team.Teams' | translate}}",
                parent: "dashboard",
            },
            permission: { partnerPermission: Permission.PartnerOperations },
            resolve: {
                translateReady,
            },
            url: "/teams",
        };

        const teamDetailsState: IMmState = {
            name: "teams.team",
            ncyBreadcrumb: {
                label: "{{$$childHead.$ctrl.team.data.name}}",
                parent: "teams",
            },
            permission: { partnerPermission: Permission.PartnerOperations },
            resolve: {
                teamId: ["$transition$", ($transition$: Transition) => $transition$.params().teamId],
                translateReady,
            },
            url: "/:teamId",
            views: {
                "@": {
                    component: "mmTeamDetails",
                },
            },
        };

        const partnerUserDetailsState: IMmState = {
            name: "partnerUserDetails",
            ncyBreadcrumb: {
                label: "{{$$childHead.$ctrl.partnerUser.data.displayName}}",
                parent: "partners.partner",
            },
            permission: { partnerPermission: Permission.PartnerOperations },
            resolve: {
                partnerId: ["$transition$", ($transition$: Transition) => $transition$.params().partnerId],
                partnerUserId: ["$transition$", ($transition$: Transition) => $transition$.params().partnerUserId],
                translateReady,
            },
            url: "/partners/:partnerId/users/:partnerUserId",
            views: {
                "@": {
                    component: "mmPartnerUserDetails",
                },
            },
        };

        const partnersState: IMmState = {
            component: "partners",
            name: "partners",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Partner.Partners' | translate}}",
                parent: "dashboard",
            },
            permission: { partnerPermission: Permission.PartnerSettings },
            resolve: {
                translateReady,
            },
            url: "/partners",
        };

        const partnerDetailsState: IMmState = {
            name: "partners.partner",
            ncyBreadcrumb: {
                label: "{{$$childHead.$ctrl.partner.data.name}}",
                parent: "partners",
            },
            permission: { partnerPermission: Permission.PartnerSettings },
            resolve: {
                partnerId: ["$transition$", ($transition$: Transition) => $transition$.params().partnerId],
                translateReady,
            },
            url: "/:partnerId",
            views: {
                "@": {
                    component: "irPartnerDetailsReactWrapper",
                },
            },
        };

        const equipmentListState: IMmState = {
            component: "equipment",
            name: "equipment",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Equipment.Equipments' | translate}}",
                parent: "dashboard",
            },
            permission: { partnerPermission: Permission.PartnerOperations },
            resolve: {
                translateReady,
            },
            url: "/equipment",
        };

        const equipmentTypesState: IMmState = {
            component: "equipmentTypesList",
            name: "equipmentTypes",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.EquipmentType.EquipmentTypes' | translate}}",
                parent: "dashboard",
            },
            permission: { partnerPermission: Permission.GlobalSysAdmin },
            resolve: {
                translateReady,
            },
            url: "/equipmentTypes",
        };

        const equipmentTypeDetailsState: IMmState = {
            name: "equipmentTypes.equipmentType",
            ncyBreadcrumb: {
                label: "{{$$childHead.$ctrl.equipmentType.data.displayName}}",
                parent: "equipmentTypes",
            },
            permission: { partnerPermission: Permission.GlobalSysAdmin },
            resolve: {
                equipmentTypeId: ["$transition$", ($transition$: Transition) => $transition$.params().equipmentTypeId],
                translateReady,
            },
            url: "/:equipmentTypeId",
            views: {
                "@": {
                    component: "irEquipmentTypeDetails",
                },
            },
        };

        const equipmentFunctionalityTypeDetailsState: IMmState = {
            name: "equipmentFunctionalityType",
            ncyBreadcrumb: {
                label: "{{$$childHead.$ctrl.equipmentFunctionalityType.data.displayName}}",
                parent: "dashboard",
            },
            permission: { partnerPermission: Permission.GlobalSysAdmin },
            resolve: {
                functionalityTypeId: [
                    "$transition$",
                    ($transition$: Transition) => $transition$.params().functionalityTypeId,
                ],
                translateReady,
            },
            url: "/equipmentFunctionalityTypes/:functionalityTypeId",
            views: {
                "@": {
                    component: "irEquipmentFunctionalityTypeDetails",
                },
            },
        };

        const manufacturersState: IMmState = {
            component: "manufacturers",
            name: "manufacturers",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Manufacturer.Manufacturers' | translate}}",
                parent: "dashboard",
            },
            permission: { partnerPermission: Permission.GlobalSysAdmin },
            resolve: {
                translateReady,
            },
            url: "/manufacturers",
        };

        const companiesState: IMmState = {
            component: "companies",
            name: "companies",
            ncyBreadcrumb: {
                label: "{{'BeforeLogin.Company.Companies' | translate}}",
                parent: "dashboard",
            },
            permission: { partnerPermission: Permission.PartnerOperations },
            resolve: {
                translateReady,
            },
            url: "/companies",
        };

        const companyDetailsState: IMmState = {
            name: "companies.company",
            ncyBreadcrumb: {
                label: "{{$$childHead.$ctrl.company.data.name}}",
                parent: "companies",
            },
            permission: { partnerPermission: Permission.PartnerOperations },
            resolve: {
                companyId: ["$transition$", ($transition$: Transition) => $transition$.params().companyId],
                translateReady,
            },
            url: "/:companyId",
            views: {
                "@": {
                    component: "irCompanyDetailsReactWrapper",
                },
            },
        };

        $urlMatcherFactoryProvider.strictMode(false);
        $stateProvider
            .state(dashboardState)
            .state(sitesListState)
            .state(siteLayoutState)
            .state(siteOverviewState)
            .state(siteBasicInformationState)
            .state(siteUsersState)
            .state(siteConfigurationGroupsState)
            .state(siteDesignAndPlanningState)
            .state(siteEquipmentState)
            .state(siteEventsState)
            .state(siteActivationState)
            .state(siteSystemsTestingState)
            .state(alarmCentralsListState)
            .state(alarmCentralState)
            .state(translationKeysState)
            .state(translationsState)
            .state(aboutState)
            .state(loginState)
            .state(teamsState)
            .state(teamDetailsState)
            .state(partnerUserDetailsState)
            .state(partnersState)
            .state(partnerDetailsState)
            .state(equipmentListState)
            .state(equipmentTypesState)
            .state(equipmentTypeDetailsState)
            .state(equipmentFunctionalityTypeDetailsState)
            .state(manufacturersState)
            .state(companiesState)
            .state(companyDetailsState)
            .state(profileState);
    },
];
